/**
 * `settings` namespace — English canonical source.
 *
 * Owned by the Settings team: the hub, general/arm, Toss, AI, and push pages plus
 * the shared credential controls and the static `catalog.ts` copy (feature-gate
 * cards, provider hints, verify-status labels). The arm-switch copy is
 * SAFETY-RELEVANT — the "real money", "global kill-switch", and "requires a
 * verified Toss credential" meanings must survive translation exactly. Keys are
 * dot-free camelCase; `{kind}`/`{date}` are interpolated at the call site.
 * `ko/settings.ts` mirrors this shape (a missing key is a type error). Shared
 * actions/status reuse `common.*` instead of re-declaring here.
 */
export const settings = {
  // ── hub ──
  hubEyebrow: "Configuration",
  hubDescription: "Connect a broker and AI, register push, and arm trading. Ships disarmed.",
  dryRunDefaultOn: "New strategies default to dry-run — orders are simulated.",
  dryRunDefaultOff: "New strategies default to live orders.",
  manageArmingDefaults: "Manage arming & defaults",
  unlocked: "Unlocked",
  locked: "Locked",

  // ── feature-gate cards (catalog.ts) ──
  gateAiTitle: "AI providers",
  gateAiUnlocks: "Unlocks the strategy-builder chat and the AI advisor gate.",
  gateAiUnlockedNote: "Strategy builder and advisor enabled.",
  gateAiLockedCta: "Add an AI key",
  gateTossTitle: "Toss Securities",
  gateTossUnlocks: "Unlocks live market data, account sync, orders, and arming trading.",
  gateTossUnlockedNote: "Broker connected — trading can be armed.",
  gateTossLockedCta: "Connect Toss",
  gatePushTitle: "Mobile push",
  gatePushUnlocks: "Unlocks approval-required strategies and mobile approve/reject.",
  gatePushUnlockedNote: "Push configured — approval-required mode available.",
  gatePushLockedCta: "Set up push",

  // ── credential verify-status labels (catalog.ts) ──
  verifyVerified: "Verified",
  verifyFailed: "Failed",
  verifyUntested: "Untested",
  verifyNotSet: "Not set",

  // ── shared credential controls (CredentialControls.tsx) ──
  verifiedAt: "Verified {date}",
  testConnection: "Test connection",
  removeConfirm: "Remove the {kind} credential? This re-locks its feature.",
  testFailed: "Could not run the test. Try again.",
  connectionVerified: "Connection verified — feature unlocked.",
  verificationFailed: "Verification failed.",
  removeFailed: "Could not remove the credential.",

  // ── general · page ──
  generalEyebrow: "Settings · General",
  generalTitle: "General",
  generalDescription: "Timezone, base currency, the global arm switch, and notification delivery.",

  // ── general · language (bilingual by design so it is findable in either mode) ──
  languageTitle: "언어 / Language",
  languageDescription: "Interface language. Applies immediately and is remembered on this device.",
  languageHint: "한국어 / English",

  // ── general · operational defaults ──
  defaultsTitle: "Operational defaults",
  defaultsDescription:
    "The clock strategies schedule against, the reporting base currency, and the safe default for new strategies.",
  timezoneLabel: "Timezone",
  timezoneHint: "IANA timezone name.",
  baseCurrencyLabel: "Base currency",
  baseCurrencyHint: "Report base for portfolio decomposition.",
  currencyKrw: "KRW — Korean won",
  currencyUsd: "USD — US dollar",
  defaultDryRunLabel: "Default new strategies to dry-run",
  defaultDryRunDescription:
    "Recommended. New strategies simulate fills instead of placing live orders (§0.5).",
  saveDefaults: "Save defaults",

  // ── general · notifications section ──
  notificationsDescription:
    "Route each event type to web, push, or email, and set a priority floor and quiet hours.",

  // ── general · live trading (arm master switch — SAFETY) ──
  liveTradingTitle: "Live trading",
  liveTradingDescription:
    "The global kill-switch. While disarmed, no live order is ever placed — every order is simulated regardless of a strategy's own mode.",
  armedBadge: "Armed",
  disarmedBadge: "Disarmed",
  armLiveTrading: "Arm live trading",
  armDescriptionArmed: "Live orders can be placed by armed, non-dry-run strategies.",
  armDescriptionDisarmed: "Turn on to allow live orders. Requires a verified Toss credential.",
  tossRequiredBeforeArm: "A verified Toss credential is required before arming.",
  connectTossLink: "Connect Toss →",
  armErrorNotReady:
    "A verified Toss credential is required before arming (connect it under Toss settings).",
  armErrorGeneric: "Could not update trading state. Try again.",
  confirmHeading: "This enables real orders",
  confirmBody:
    "Once armed, any active strategy that is not in dry-run may submit live orders to your Toss account using real money. Strategies still respect their own dry-run flag and risk limits; this switch only removes the global block.",
  confirmAck: "I understand that live orders spend real money.",

  // ── AI page ──
  aiEyebrow: "Settings · AI",
  aiDescription:
    "Add an OpenAI or Anthropic key to unlock the strategy builder and advisor. At least one verified provider unlocks the feature.",
  openaiKeyHint: "A standard OpenAI API key (Bearer). Stored encrypted; never returned.",
  anthropicKeyHint:
    "An `x-api-key` credential by default. Use a dev-platform bearer only if you have one.",
  defaultModelPrefix: "Default model:",
  credentialTypeLabel: "Credential type",
  credentialTypeApiKey: "API key (x-api-key)",
  credentialTypeBearer: "Dev-platform bearer",
  defaultModelLabel: "Default model",
  bearerTokenLabel: "Bearer token",
  apiKeyLabel: "API key",
  replaceStoredHint: "Enter a new value to replace the stored credential.",
  storedEncryptedHint: "Stored encrypted (AES-256-GCM). Never returned to the browser.",
  betaHeaderLabel: "Beta header (optional)",
  betaHeaderHint: "Sent as `anthropic-beta` for dev-platform bearers.",
  replaceKey: "Replace key",
  saveKey: "Save key",
  saveCredentialFailed: "Could not save the credential.",

  // ── AI page · custom (OpenAI-compatible) provider ──
  customTitle: "Custom (OpenAI-compatible)",
  customKeyHint:
    "Any OpenAI-compatible API — OpenRouter, Ollama Cloud, local Ollama, LM Studio, vLLM. For a keyless local server, enter any placeholder value as the key.",
  baseUrlLabel: "Base URL",
  baseUrlHint: "The OpenAI-compatible endpoint, e.g. https://openrouter.ai/api/v1.",
  customApiKeyHint: "Bearer key for the endpoint. Keyless local servers: enter any value.",
  baseUrlRequired: "A Base URL is required.",

  // ── AI page · model aliases ──
  aliasesTitle: "Model aliases",
  aliasesDescription:
    "Pick which provider and model back each internal role. Providers you have registered above are selectable; the model box autocompletes from that provider’s models — you can also type any model id.",
  aliasChatBuilder: "Chat builder",
  aliasAdvisor: "AI advisor",
  aliasClassify: "Classify",
  aliasProviderLabel: "Provider",
  aliasModelLabel: "Model",
  aliasProviderCustom: "Custom (OpenAI-compatible)",
  aliasProviderUnregistered: " (not registered)",
  aliasModelsLoading: "Loading available models…",
  aliasModelsError: "Could not load the model list — enter one manually.",
  aliasUsingDefault: "Built-in default:",
  aliasesHint:
    "Register a provider above to unlock its model list. Anything you type still works as a fallback.",
  saveAliases: "Save aliases",
  saveAliasesFailed: "Could not save the model aliases.",

  // ── agent tools page ──
  toolsEyebrow: "Settings · Agent tools",
  toolsTitle: "Agent tools",
  toolsDescription:
    "Choose which tools each AI agent may use. A disabled tool is never offered to the model; changes apply from the next chat turn or advisor decision.",
  toolsHubDescription: "Enable or disable individual tools for the chat builder and the advisor.",
  manageAgentTools: "Manage agent tools",
  toolsChatTitle: "Chat agent (strategy builder)",
  toolsChatDescription:
    "Tools available to the strategy-builder chat. Lazy-loaded tools stay out of the prompt until the model discovers them via tool_search.",
  toolsAdvisorTitle: "Advisor agent",
  toolsAdvisorDescription:
    "Investigation tools the AI advisor may call before approving, resizing, or vetoing orders. With all disabled the advisor decides in a single shot.",
  toolCoreBadge: "Core",
  toolLazyBadge: "Lazy-loaded",
  toolCoreHint: "Required for the agent to function — cannot be disabled.",
  saveTools: "Save changes",
  saveToolsFailed: "Could not save the tool settings.",
  toolsUnsavedHint: "Unsaved changes.",

  // ── agent tools · per-tool descriptions (slot names stay English) ──
  toolDescEditStrategy: "Edits the strategy draft — the builder's core mechanism.",
  toolDescWebSearch: "Searches the web for current context (news, earnings, macro).",
  toolDescFetchPage: "Fetches a page surfaced by a web search for more detail.",
  toolDescSaveStrategy: "Saves the draft as (or onto) a strategy.",
  toolDescRunBacktest: "Queues an asynchronous backtest of the linked strategy.",
  toolDescLoadStrategyStats: "Loads the live win-rate / payoff stats of a strategy.",
  toolDescLoadBacktestResults: "Loads the most recent completed backtest's metrics.",
  toolDescLoadAdvisorDecisions: "Loads past advisor verdicts for prompt tuning.",
  toolDescResearchWeb: "Spawns a bounded web-research sub-agent for a topic.",
  toolDescSnapshotSchedule: "Reads or edits the automatic portfolio-snapshot schedule.",
  toolDescTakeSnapshot: "Records a portfolio snapshot into history right now.",
  toolDescGetTrend: "Recent daily price-trend summary for a symbol.",
  toolDescGetHoldings: "The full current account holdings with P&L.",
  toolDescGetTradeHistory: "The strategy's recent executed fills.",
  toolDescGetDecisionHistory: "The strategy's recent advisor decisions.",
  toolDescGetPortfolioSummary: "Realized-P&L track record plus overall account return.",

  // ── Toss page ──
  tossEyebrow: "Settings · Broker",
  tossDescription:
    "Connect your Toss OpenAPI credentials to unlock live market data, trading, and account sync.",
  apiCredentialsTitle: "API credentials",
  apiCredentialsDescription:
    "Issued in Toss Securities WTS under Settings → Open API. Both values are stored encrypted; only a masked tail is ever shown.",
  // Toss's own console calls these "API Key" / "Secret Key" — mirror that naming
  // (they are sent as OAuth client_id / client_secret under the hood).
  clientIdLabel: "API Key (tsck_…)",
  clientSecretLabel: "Secret Key (tssk_…)",
  replaceCredentials: "Replace credentials",
  saveCredentials: "Save credentials",
  saveCredentialsFailed: "Could not save the credentials.",
  accountsTitle: "Accounts",
  accountsLockedDescription: "Sync your Toss accounts once the broker credential is verified.",
  verifyTossToSync: "Verify Toss to sync accounts",
  verifyTossToSyncDescription:
    "Test the credential above. A verified connection unlocks account sync and, in General settings, the ability to arm live trading.",
  accountsDescription: "Cached from Toss (getAccounts is limited to 1 req/s). Sync to refresh.",
  syncNow: "Sync now",
  syncFailed: "Sync failed. Try again.",
  noAccounts: "No accounts yet",
  noAccountsDescription: "Run a sync to pull your Toss accounts.",
  syncAccounts: "Sync accounts",
  primaryBadge: "Primary",

  // ── push page ──
  pushEyebrow: "Settings · Push",
  pushDescription:
    "Configure push to enable approval-required strategies and mobile approve/reject.",
  pushUnlocked: "Push unlocked",
  pushLocked: "Push locked",
  expoTokenTitle: "Expo access token",
  expoTokenDescription:
    "Optional token for Expo's enhanced push security. Verifying it unlocks the push feature gate.",
  accessTokenLabel: "Access token",
  accessTokenPlaceholder: "Expo access token",
  replaceTokenHint: "Enter a new value to replace the stored token.",
  tokenStoredHint:
    "Stored encrypted; sends require no server key, but a verified token unlocks the gate.",
  replaceToken: "Replace token",
  saveToken: "Save token",
  saveTokenFailed: "Could not save the token.",
  prerequisitesTitle: "Prerequisites",
  prerequisitesDescription:
    "Approval-required mode stays gated until push is fully configured. These cannot be skipped.",
  prereqAndroidTitle: "Android — FCM V1 service-account JSON",
  prereqAndroidDetail: "Upload the Firebase Cloud Messaging V1 service account to EAS credentials.",
  prereqIosTitle: "iOS — APNs key + paid Apple Developer account",
  prereqIosDetail: "An Apple Push key and a paid membership; EAS manages the certificates.",
  prereqDeviceTitle: "Register a device from the mobile app",
  prereqDeviceDetail:
    "Open the richrich app, grant notifications, and it posts its Expo push token.",

  // ── mobile QR sign-in (general page) ──
  qrSignInTitle: "Mobile app sign-in",
  qrSignInDescription:
    "Sign the richrich app in without typing a code: generate a QR and scan it with your phone's camera.",
  qrSignInGenerate: "Generate QR code",
  qrSignInRegenerate: "Generate a new code",
  qrSignInScanHint: "Scan with your phone's camera — the richrich app opens and signs in.",
  qrSignInExpiresIn: "Single-use · expires in {seconds}s",
  qrSignInExpired: "This code has expired.",
  qrSignInFailed: "Could not generate a QR code. Try again.",
} as const;

export type SettingsKey = keyof typeof settings;
